import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/content';

/**
 * SEE WHAT'S INSIDE — real pages, one per slide. Swipe on touch, arrows and
 * dots on desktop. Uses native scroll-snap so it stays smooth everywhere.
 */
export function InsidePagesCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = insidePages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    track.scrollTo({
      left: next * track.clientWidth,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const index = Math.round(track.scrollLeft / track.clientWidth);
      setActive(Math.min(Math.max(index, 0), total - 1));
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, [total]);
  return <div className="relative mx-auto max-w-5xl" aria-roledescription="carousel" aria-label="Pages from Nail Educator Ready" data-id="element-19" data-mp="bitfxtll6cfg">
      <div className="overflow-hidden rounded-[20px] border border-brand-charcoal/10 bg-white shadow-[0_40px_80px_-40px_rgba(90,70,40,0.45)]" data-id="element-20" data-mp="gbd3njdzmnxz">
        <div ref={trackRef} className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto" data-id="element-21" data-mp="3jrjw0tqycyrg">
          {insidePages.map((page, index) => <figure key={page.src} className="w-full flex-none snap-center" aria-roledescription="slide" aria-label={`${index + 1} of ${total}: ${page.caption}`} data-id="element-22" data-mp="3os7ws74zgtyv" data-mpi={index}>
              <img src={page.src} alt={`${page.caption} — a real page from Nail Educator Ready`} width={1600} height={1000} loading={index === 0 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="block aspect-[16/10] h-auto w-full select-none object-cover" data-id="element-23" data-mp="18zs676fxndsf" />
            </figure>)}
        </div>
      </div>

      <ArrowButton side="left" label="Previous page" onClick={() => goTo(active - 1)} data-id="element-24" data-mp="3e323uswi2mf1">
        <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-25" data-mp="3e18uv6yezq3v" />
      </ArrowButton>
      <ArrowButton side="right" label="Next page" onClick={() => goTo(active + 1)} data-id="element-26" data-mp="2qb4qgxdejmbt">
        <ChevronRightIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-27" data-mp="2m9sywmaywr0z" />
      </ArrowButton>

      <div className="mt-6 flex flex-col items-center gap-3" data-id="element-28" data-mp="305df39zbav4p">
        <p className="font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-charcoal/55" aria-live="polite" data-id="element-29" data-mp="2vbshhj1ms361">
          {insidePages[active].caption}
        </p>
        <div className="flex items-center gap-2.5" data-id="element-30" data-mp="22oohjsheaf14">
          {insidePages.map((page, index) => <button key={page.src} type="button" aria-label={`Show ${page.caption}`} aria-current={index === active} onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta focus-visible:ring-offset-2 ${index === active ? 'w-8 bg-brand-cta' : 'w-2 bg-brand-charcoal/20 hover:bg-brand-charcoal/35'}`} data-id="element-31" data-mp="i5m1mlqkgl7k" data-mpi={index} />)}
        </div>
      </div>
    </div>;
}
function ArrowButton({
  side,
  label,
  onClick,
  children
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className={`absolute top-[calc(50%-2.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-brand-charcoal/10 bg-white text-brand-charcoal shadow-[0_10px_24px_-12px_rgba(42,37,32,0.4)] transition-[background-color,transform] duration-150 ease-out hover:bg-brand-ivory active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta md:flex ${side === 'left' ? '-left-6' : '-right-6'}`} data-id="element-32" data-mp="3cld820iu07jx">
      {children}
    </button>;
}